import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { IconButton, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState } from "../components/EmptyState";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageContent } from "../components/PageContent";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../components/Table";
import { CatalogListName } from "../components/CatalogOriginChip";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { useShowCatalogOriginMarkers } from "../hooks/useShowCatalogOriginMarkers";
import { CATALOG_NEW_ITEM_ID } from "./catalog/catalogNav";
import {
  useGetOffersQuery,
  useDeleteOfferMutation,
} from "../query/offersQuery";
import type { OfferId } from "@KassaroAB/shared/types/ids";

export const OffersView = () => {
  const { t } = useTranslation();
  const { scope, pagePath } = useCatalogScope("offers");
  const showCatalogOrigin = useShowCatalogOriginMarkers(scope);

  const [deleteId, setDeleteId] = useState<OfferId | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    data: offers,
    isLoading,
    error: loadError,
  } = useGetOffersQuery(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteOfferMutation(scope);

  const offerList = offers ?? [];
  const deleteName =
    offerList.find((offer) => offer.id === deleteId)?.name ?? "";

  const confirmDelete = () => {
    if (!deleteId) return;
    deleteMutation(deleteId, {
      onSuccess: () => {
        setSuccessMessage(t("offersView.successDeleted"));
        setDeleteId(null);
      },
      onError: () => setSuccessMessage(null),
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  if (isLoading && offerList.length === 0) {
    return (
      <PageContent title={t("offersView.title")}>
        <LoadingContainer message={t("offersView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={t("offersView.title")}
      actions={
        <RouterButtonLink to={`${pagePath}/${CATALOG_NEW_ITEM_ID}`}>
          <Plus size={16} aria-hidden />
          {t("offersView.create")}
        </RouterButtonLink>
      }
    >
      {loadError && (
        <InfoBox variant="error">{t("offersView.errorLoad")}</InfoBox>
      )}
      {deleteError && (
        <InfoBox variant="error">{t("offersView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      {offerList.length > 0 ? (
        <Card>
          <Table minWidth="640px">
            <TableHeader>
              <tr>
                <TableHeaderCell>{t("offersView.tableName")}</TableHeaderCell>
                <TableHeaderCell>{t("offersView.tableType")}</TableHeaderCell>
                <TableHeaderCell>
                  {t("offersView.tableEnabled")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("offersView.tablePriority")}
                </TableHeaderCell>
                <TableHeaderCell align="right">{null}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {offerList.map((offer) => (
                <TableRow key={offer.id} to={`${pagePath}/${offer.id}`}>
                  <TableCell noWrap>
                    <CatalogListName
                      name={offer.name}
                      origin={offer.origin}
                      showOrigin={showCatalogOrigin}
                    />
                  </TableCell>
                  <TableCell noWrap>
                    {t(`offerForm.types.${offer.type}`)}
                  </TableCell>
                  <TableCell noWrap>
                    {offer.enabled
                      ? t("offersView.enabledYes")
                      : t("offersView.enabledNo")}
                  </TableCell>
                  <TableCell noWrap>{String(offer.priority)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      type="button"
                      $color="danger"
                      aria-label={t("common.delete")}
                      disabled={isDeletePending}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setDeleteId(offer.id);
                      }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <EmptyState
            message={`${t("offersView.empty")} ${t("offersView.emptyHint")}`}
          />
        </Card>
      )}
      <ConfirmDialog
        isOpen={deleteId != null}
        title={t("offerForm.deleteOffer")}
        message={t("offersView.deleteConfirm", { name: deleteName })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </PageContent>
  );
};
